// src/components/UI.jsx
// Shared primitive UI components used throughout the app

import React, { useState, useEffect, useMemo } from "react";
import { COLOR_HEX, COLOR_CHOICES } from "../lib/constants";
import { EMOJI_DB } from "../data/emojis";
import { searchEmojiList } from "../utils/emojiSearch";

export const Button = ({children,className="",variant="default",size="default",onClick,disabled}) => {
  const v={default:"bg-neutral-900 text-white hover:bg-neutral-800",secondary:"bg-neutral-200 hover:bg-neutral-300",ghost:"hover:bg-neutral-200",destructive:"bg-red-600 text-white hover:bg-red-700",outline:"border border-neutral-300 hover:bg-neutral-100",primary:"bg-blue-600 text-white hover:bg-blue-700",danger:"border border-red-300 text-red-600 hover:bg-red-50"};
  const s={default:"h-9 px-4 py-2",sm:"h-8 px-3 text-xs",icon:"h-9 w-9"};
  return <button onClick={onClick} disabled={disabled} className={`inline-flex items-center justify-center rounded-md text-sm font-medium transition ${v[variant]} ${s[size]} ${disabled?'opacity-50 cursor-not-allowed':''} ${className}`}>{children}</button>;
};

export const Card = ({children,className=""}) => <div className={`bg-white rounded-2xl shadow border ${className}`}>{children}</div>;
export const CardHeader = ({children,className=""}) => <div className={`p-3 ${className}`}>{children}</div>;
export const CardContent = ({children,className=""}) => <div className={`p-3 ${className}`}>{children}</div>;
export const CardTitle = ({children,className=""}) => <h3 className={`font-semibold ${className}`}>{children}</h3>;
export const Badge = ({children,className="",variant="default"}) => {
  const v={default:"bg-neutral-900 text-white",secondary:"bg-neutral-200 text-neutral-900",outline:"border border-neutral-300"};
  return <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${v[variant]} ${className}`}>{children}</span>;
};
export const Input = ({className="",type="text",value,onChange,placeholder,maxLength,readOnly,onFocus,autoFocus,required,inputMode,pattern,onKeyDown,name}) => (
  <input type={type} value={value} onChange={onChange} placeholder={placeholder} maxLength={maxLength}
    readOnly={readOnly} onFocus={onFocus} autoFocus={autoFocus} required={required}
    inputMode={inputMode} pattern={pattern} onKeyDown={onKeyDown}
    name={name||`field-${Math.random()}`} autoComplete={type==='password'?'new-password':'off'}
    className={`flex h-9 w-full rounded-md border border-neutral-300 bg-white px-3 py-1 text-sm placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${className}`}/>
);
export const Label = ({children,className=""}) => <label className={`block text-sm font-medium mb-1 ${className}`}>{children}</label>;
export const Select = ({children,value,onValueChange,className=""}) => (
  <select value={value} onChange={e=>onValueChange(e.target.value)} className={`flex h-9 w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${className}`}>{children}</select>
);
export const SelectItem = ({children,value,disabled}) => <option value={value} disabled={disabled}>{children}</option>;

export const Progress = ({value,className="",fillColor,goalEmoji}) => (
  <div className={`relative h-3 w-full rounded-full bg-neutral-200 ${className}`} style={{overflow:'visible'}}>
    <div className="h-full rounded-full transition-all" style={{width:`${Math.min(100,value)}%`,backgroundColor:fillColor||'#171717'}}/>
    {goalEmoji&&(
      <span className="emoji absolute pointer-events-none" style={{
        fontSize:'1.5rem', lineHeight:1, top:'50%',
        left:`calc(${Math.min(100,value)}% - 0.75rem)`,
        transform:'translateY(-50%)',
        filter:'drop-shadow(0 1px 2px rgba(0,0,0,.35))', zIndex:1,
      }}>{goalEmoji}</span>
    )}
  </div>
);

export const Modal = ({open,onClose,children,zIndex=50,wide=false}) => {
  if(!open) return null;
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      style={{zIndex}} onClick={onClose}>
      <div className={`w-full ${wide?'max-w-2xl':'max-w-lg'} bg-white rounded-2xl shadow-xl border overflow-hidden`}
        style={{position:'relative',zIndex:zIndex+10}} onClick={e=>e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
};
export const ModalHeader = ({children}) => <div className="p-4 border-b">{children}</div>;
export const ModalTitle = ({children}) => <h2 className="text-lg font-semibold">{children}</h2>;
export const ModalBody = ({children,className=""}) => <div className={`p-4 ${className}`}>{children}</div>;
export const ModalFooter = ({children}) => <div className="p-4 border-t flex justify-end gap-2">{children}</div>;

export const ColorSwatches = ({value,onChange}) => (
  <div className="flex gap-2 flex-wrap mt-1">
    {Object.entries(COLOR_HEX).map(([cls,hex]) => (
      <div key={cls} className="flex flex-col items-center gap-0.5">
        <button type="button" onClick={()=>onChange(cls)} title={COLOR_CHOICES[cls]}
          style={{backgroundColor:hex}}
          className={`w-7 h-7 rounded-full transition-all ${value===cls?'ring-2 ring-offset-1 ring-neutral-700 scale-110':''}`}/>
        <span className="text-[9px] text-neutral-500 leading-none">{COLOR_CHOICES[cls]}</span>
      </div>
    ))}
  </div>
);

export const AccordionSection = ({title,defaultOpen=false,children}) => {
  const [open,setOpen] = useState(defaultOpen);
  return (
    <div className="border rounded-xl overflow-hidden mb-3">
      <button onClick={()=>setOpen(v=>!v)} className="w-full flex items-center justify-between p-3 bg-neutral-50 hover:bg-neutral-100 transition text-left">
        <span className="font-semibold text-sm">{title}</span>
        <span className="text-neutral-400 text-lg leading-none">{open?'▲':'▼'}</span>
      </button>
      {open&&<div className="p-3 border-t">{children}</div>}
    </div>
  );
};

export function ConfettiBurst({x,y,onDone}){
  const [p]=useState(()=>Array.from({length:30}).map((_,i)=>({id:i,x,y,dx:(Math.random()-.5)*200,dy:-Math.random()*180-60,hue:Math.floor(Math.random()*360),size:4+Math.random()*4})));
  useEffect(()=>{const t=setTimeout(onDone,1000);return()=>clearTimeout(t);},[onDone]);
  return <div className="pointer-events-none fixed inset-0 z-50">{p.map(q=><div key={q.id} style={{position:"absolute",left:q.x,top:q.y,width:q.size,height:q.size,background:`hsl(${q.hue} 90% 55%)`,animation:`confetti 0.8s ease-out forwards`,"--dx":`${q.dx}px`,"--dy":`${q.dy}px`}}/>)}</div>;
}

export function EmojiPickerModal({open,onClose,list,onSelect,zIndex=60}){
  const [query,setQuery] = useState('');
  useEffect(()=>{ if(!open) setQuery(''); },[open]);
  const results = useMemo(()=>searchEmojiList(query,list,EMOJI_DB),[query,list]);
  if(!open) return null;
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      style={{zIndex}} onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl border overflow-hidden"
        style={{position:'relative',zIndex:zIndex+10}} onClick={e=>e.stopPropagation()}>
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold mb-2">Select Emoji</h2>
          <input autoFocus value={query} onChange={e=>setQuery(e.target.value)}
            placeholder="Search: hair, bed, teeth, trophy…"
            className="flex h-9 w-full rounded-md border border-neutral-300 bg-white px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
        </div>
        <div className="p-3 grid grid-cols-8 gap-1.5 max-h-72 overflow-y-auto">
          {results.length===0&&<div className="col-span-8 text-center text-sm text-neutral-400 py-6">No emojis found for "{query}"</div>}
          {results.map((e,i)=>(
            <button key={i} onClick={()=>{onSelect(e);onClose();}}
              title={EMOJI_DB.find(d=>d.e===e)?.k||''}
              className="emoji text-3xl p-1.5 rounded-lg hover:bg-neutral-200 transition flex items-center justify-center">
              {e}
            </button>
          ))}
        </div>
        {query&&<div className="px-4 pb-3 text-xs text-neutral-400">Results for "{query}" · <button className="text-blue-500 hover:underline" onClick={()=>setQuery('')}>clear</button></div>}
      </div>
    </div>
  );
}